import {
  buildImportCompletionShellNotice,
  buildImportFormData,
  buildImportSubmissionState,
  createInitialImportSubmissionState,
  resolveVisibleImportImpact,
  shouldInvalidateImportCache,
} from "../../utils/importFilesPageState.js";
import {
  buildImportNetworkFailureFeedback,
  resolveImportSessionAccessState,
  resolveImportSubmissionValidationState,
} from "./importValidations.js";
import { resolveImportBackendFeedbackState } from "../mappers/importBackendToIssueMapper.js";
import { fetchWithOrderDuplicateConfirmation, isOrderDuplicateConfirmationCancelled } from "../../utils/orderDuplicateConfirmation.js";

const DEFAULT_IMPORT_REQUEST_URL = "/api/import";

function getDefaultFetch() {
  if (typeof globalThis.fetch !== "function") {
    throw new Error("Import indisponible: fetch n'est pas supporté.");
  }

  return globalThis.fetch.bind(globalThis);
}

async function getDefaultSession() {
  const { getSupabaseSession } = await import("../../lib/supabaseClient.js");
  return getSupabaseSession();
}

async function parseImportResponse(response) {
  const rawResponseText = await response.text();
  let payload = null;

  try {
    payload = rawResponseText ? JSON.parse(rawResponseText) : null;
  } catch {
    payload = null;
  }

  return {
    payload,
    rawResponseText,
  };
}

async function snapshotImportFile(file) {
  if (!file || typeof file.arrayBuffer !== "function" || typeof File !== "function") {
    return file ?? null;
  }

  const fileBuffer = await file.arrayBuffer();
  return new File(
    [fileBuffer],
    typeof file.name === "string" && file.name ? file.name : "import.xlsx",
    {
      lastModified: Number.isFinite(file.lastModified) ? file.lastModified : Date.now(),
      type: typeof file.type === "string" ? file.type : "",
    },
  );
}

async function snapshotImportFilesByField(filesByField) {
  if (!filesByField || typeof filesByField !== "object" || Array.isArray(filesByField)) {
    return {};
  }

  const nextEntries = await Promise.all(
    Object.entries(filesByField).map(async ([field, file]) => [field, await snapshotImportFile(file)]),
  );

  return Object.fromEntries(nextEntries);
}

function resolveImportSubmissionStatus({ feedback, responseOk = false } = {}) {
  switch (feedback?.issue?.code) {
    case "import.selection_required":
      return "validation-error";
    case "import.session_unavailable":
      return "session-error";
    case "import.network_error":
      return "network-error";
    case "import.confirmation_required":
      return "confirmation-required";
    default:
      return responseOk ? "success" : "backend-error";
  }
}

export async function submitImportRequest({
  bypassOrderImportGuards = false,
  confirmBreakingImport = false,
  fieldConfigs,
  filesByField,
  fetchImpl = getDefaultFetch(),
  getSession = null,
  previousState = createInitialImportSubmissionState(),
  reportProgress = null,
  onDuplicateConfirmationChange = null,
  requestUrl = DEFAULT_IMPORT_REQUEST_URL,
  resolveResultLabel = (field) => field || "Requête",
  selectedFileCount = 0,
  submissionMode = "selection",
} = {}) {
  const selectionRequiredState = resolveImportSubmissionValidationState({
    selectedFileCount,
  });
  if (selectionRequiredState) {
    return buildImportSubmissionState({
      feedback: selectionRequiredState.feedback,
      impact: null,
      results: selectionRequiredState.results,
      status: "validation-error",
    });
  }

  try {
    const resolveSession = typeof getSession === "function"
      ? getSession
      : getDefaultSession;
    const { error, session } = await resolveSession();
    const sessionAccessState = resolveImportSessionAccessState({
      error,
      session,
    });

    if (sessionAccessState.state) {
      return buildImportSubmissionState({
        feedback: sessionAccessState.state.feedback,
        impact: previousState?.impact ?? null,
        results: sessionAccessState.state.results,
        status: "session-error",
        submissionMode,
      });
    }

    const snapshottedFilesByField = await snapshotImportFilesByField(filesByField);
    const formData = buildImportFormData({
      bypassOrderImportGuards,
      fieldConfigs,
      filesByField: snapshottedFilesByField,
      confirmBreakingImport,
      submissionMode,
    });
    reportProgress?.("process");
    const response = await fetchWithOrderDuplicateConfirmation(fetchImpl, requestUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionAccessState.accessToken}`,
      },
      body: formData,
    }, { onConfirmationChange: onDuplicateConfirmationChange });
    const { payload, rawResponseText } = await parseImportResponse(response);
    const {
      feedback,
      responseImpact,
      results,
    } = resolveImportBackendFeedbackState({
      payload,
      rawResponseText,
      response,
      submissionMode,
    });
    const isConfirmationPending = feedback?.issue?.code === "import.confirmation_required";
    const isAppliedResponse = response.ok && !isConfirmationPending;

    return buildImportSubmissionState({
      completionNotice: isAppliedResponse
        ? buildImportCompletionShellNotice({
          impact: responseImpact,
          responseOk: isAppliedResponse,
          results,
          resolveResultLabel,
          submissionMode,
        })
        : null,
      feedback,
      impact: resolveVisibleImportImpact({
        responseOk: isAppliedResponse,
        impact: responseImpact,
      }),
      results,
      shouldInvalidateCache: shouldInvalidateImportCache(results),
      shouldResetSelection: isAppliedResponse,
      status: resolveImportSubmissionStatus({
        feedback,
        responseOk: isAppliedResponse,
      }),
      submissionMode,
    });
  } catch (error) {
    if (isOrderDuplicateConfirmationCancelled(error)) {
      return buildImportSubmissionState({ status: "cancelled" });
    }
    const networkFailureState = buildImportNetworkFailureFeedback(error);

    return buildImportSubmissionState({
      feedback: networkFailureState.feedback,
      impact: null,
      results: networkFailureState.results,
      status: "network-error",
    });
  }
}
